import React, { useContext, useEffect, useState, useMemo } from "react";
import useSWR from "swr";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Modal,
  ModalBody,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import classNames from "classnames";
import ButtonIcon from "../common/ButtonIcon";
import { Controller, useForm } from "react-hook-form";
import NumberFormat from "react-number-format";
import FullPagination from "../common/pagination/FullPagination";
import SelectProviderListItem from "../common/SelectProviderListItem";
import { faUndo, faSearch, faCog, faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchAccountingAreas, createAccountingArea } from "../../helpers/api/accountingAreas";
import { fetchAccountPlans } from "../../helpers/api/accountPlans";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { Toast } from "../UI/toast/Toast";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams, useLocation } from "react-router";
import { PreloadState } from "../UI/preloading/PreloadState";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import SelectPropertyListItem from "../common/SelectPropertyListItem";
import { fetchProviders, createProvider } from "../../helpers/api/provider";
import CreateProviderForm from "../providers/CreateProviderForm";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { CurrencyContext } from "../../context/Context";
import useModuleBase from "../../helpers/hooks/useModuleBase";

const CreateAccountPayForm = ({
  commonCost,
  handleSubmitData,
  handlerM,
  setRedirectUrl,
  debttopayId,
  properties,
  common,
  metaa,
}) => {
  const moduleBase = useModuleBase();

  const { id } = useParams();
  const history = useHistory();
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    watch,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
  });

  const [providerSearch, setProviderSearch] = useState("");
  const [params, setParams] = useState({
    condominiumId: id,
    search: null,
  });

  const applyProviderSearch = (value = providerSearch) => {
    const term = typeof value === "string" ? value.trim() : "";
    setProviderSearch(term);
    setParams((prev) => ({ ...prev, search: term || null }));
  };
  const { baseCurrency } = useContext(CurrencyContext);

  const {
    mutate,
    data,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchProviders, params);

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [createProviderModalOpen, setCreateProviderModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [amount, setAmount] = useState(null);
  const [description, setDescription] = useState("");
  const [accountingAreas, setAccountingAreas] = useState([]);
  const [accountPlans, setAccountPlans] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [createAccountAreaModalOpen, setCreateAccountAreaModalOpen] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");
  const [selectedAccountPlanId, setSelectedAccountPlanId] = useState("");
  const [pendingAreaId, setPendingAreaId] = useState(null);
  const {
    condominium: { selectedCondominium },
  } = useSelector((state) => state);

  const { state } = useLocation();

  useEffect(() => {
    if (selectedProvider) {
      setValue("provider_id", selectedProvider);
      clearErrors("provider_id");
    }
  }, [selectedProvider, setValue, clearErrors]);

  useEffect(() => {
    if (common && accountingAreas.length > 0) {
      const areaId = common.sub_expenses?.[0]?.accounting_area?.id || "";
      setValue("accounting_area_id", areaId);
    }
  }, [common, accountingAreas, setValue]);

  useEffect(() => {
    if (pendingAreaId && accountingAreas.some((area) => area.id === pendingAreaId)) {
      setValue("accounting_area_id", pendingAreaId, { shouldValidate: true });
      setPendingAreaId(null);
    }
  }, [accountingAreas, pendingAreaId, setValue]);

  useEffect(() => {
    setRedirectUrl(`/dashboard/providers`);
  }, [setRedirectUrl]);

  useEffect(() => {
    if (common) {
      /*   setSelectedProperty(common.provider.id); */
      setAmount(common.value);
      setDescription(common.description);
    }
    // eslint-disable-next-line
  }, [common]);

  // useSWR para obtener datos de forma eficiente y evitar duplicados
  const { data: areasRes, mutate: mutateAreas } = useSWR(id ? [id, "accounting-areas"] : null, () => fetchAccountingAreas(id));
  const { data: plansRes } = useSWR(id ? [id, "account-plans"] : null, () => fetchAccountPlans(id));

  useEffect(() => {
    if (areasRes && areasRes.data) setAccountingAreas(areasRes.data);
  }, [areasRes]);

  useEffect(() => {
    if (plansRes && plansRes.data) setAccountPlans(plansRes.data);
  }, [plansRes]);

  const getAccountingData = async () => {
    mutateAreas();
  };

  const handleCreateAccountingArea = async () => {
    if (!newAreaName) return;
    try {
      let planId = selectedAccountPlanId;
      if (!planId) {
        const defaultPlan = accountPlans.find(p => p.code === '6.1');
        planId = defaultPlan ? defaultPlan.id : null;
      }

      const response = await createAccountingArea({
        condominium_id: id,
        name: newAreaName,
        account_plan_id: planId,
      });
      if (response.code === SUCCESS_RESPONSE.OK || response.code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title: "Área creada con éxito",
        });
        setNewAreaName("");
        setSelectedAccountPlanId("");
        setCreateAccountAreaModalOpen(false);
        setPendingAreaId(response.data?.id ?? null);
        mutateAreas();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleCreateProviderSubmit = async (values) => {
    try {
      const response = await createProvider(id, values);
      const isSuccess =
        response?.code === SUCCESS_RESPONSE.CREATED ||
        response?.code === SUCCESS_RESPONSE.OK ||
        response?.data?.id;

      if (isSuccess) {
        setCreateProviderModalOpen(false);
        Toast.fire({
          type: "success",
          title: "Se ha creado el proveedor satisfactoriamente.",
        });
        const createdId = response?.data?.id;
        if (createdId) {
          setSelectedProvider(createdId);
          setValue("provider_id", createdId, { shouldValidate: true });
          applyProviderSearch(response.data.name);
        }
        mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const renderProvidersItems = () => {
    return data.map((provider) => {
      return (
        <SelectProviderListItem
          avatar={"https://picsum.photos/200/200"}
          onSelect={() => {
            if (!commonCost) {
              setSelectedProvider(provider.id);
            }
          }}
          /*             onSelect={setSelectedProperty} */
          selected={selectedProvider}
          id={provider.id}
          key={provider.id}
          title={provider.name}
          toDeleteId={provider.id}
          toEditId={provider.id}
          createdAt={provider.created_at}
          alternativeName="proveedor"
        />
      );
    });
  };

  const renderProvidersItemsAdd = () => {
    return data.map((provider) => {
      return (
        <SelectPropertyListItem
          avatar={"https://picsum.photos/200/200"}
          onSelect={() => {
            setSelectedProvider(provider.id);
          }}
          selected={selectedProvider}
          id={provider.id}
          key={provider.id}
          title={provider.name}
          toDeleteId={provider.id}
          toEditId={provider.id}
          createdAt={provider.created_at}
          alternativeName="proveedor"
        />
      );
    });
  };


  useEffect(() => {
    if (common) {
      const defaultValues = {};
      defaultValues.name = common?.name;
      defaultValues.description = common?.description;
      defaultValues.value = common?.value;
      defaultValues.type_payment_supplier = common?.type_payment_supplier;
      reset({ ...defaultValues });
    }
  }, [common, reset]);

  const renderForm = () => {
    return (
      <>
        <FalconCardHeader title="Detalles de la cuenta por pagar" light={true} />
        <CardBody>
          <FormGroup>
            <Label for="name">Nombre</Label>
            <Input
              autoComplete="off"
              id="name"
              name="name"
              innerRef={register({
                required: "El nombre es requerido.",
                minLength: {
                  value: 4,
                  message:
                    "El nombre debe tener al menos 4 caracteres.",
                },
              })}
              placeholder="Nombre de la cuenta por pagar"
              className={classNames({
                "border-danger": errors.name?.message,
              })}
            />
            {errors.name && (
              <WizardError
                error={errors.name.message}
                className="fs--1 font-weight-normal d-block"
              />
            )}
          </FormGroup>

          <Row form>
            <Col md={12}>
              <FormGroup>
                <Label for="accounting_area_id">Cuenta / Área Contable</Label>
                <Input
                  type="select"
                  name="accounting_area_id"
                  className={classNames({
                    "border-danger": errors.accounting_area_id,
                  })}
                  innerRef={register({
                    required: "Debes seleccionar un área contable.",
                  })}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Selecciona una Cuenta / Área
                  </option>
                  {accountingAreas.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </Input>
                {errors.accounting_area_id && (
                  <WizardError
                    error={errors.accounting_area_id.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                )}
                <Button
                  color="link"
                  size="sm"
                  className="p-0 mt-1"
                  onClick={() => setCreateAccountAreaModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faCog} className="mr-1" />
                  Nueva Área/Categoría
                </Button>
              </FormGroup>
            </Col>
          </Row>

          <Modal isOpen={createAccountAreaModalOpen} toggle={() => setCreateAccountAreaModalOpen(!createAccountAreaModalOpen)}>
            <div className="modal-header">
              <h5 className="modal-title">Crear Nueva Área / Categoría de Gasto</h5>
              <button type="button" className="close" onClick={() => setCreateAccountAreaModalOpen(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <ModalBody>
              <FormGroup>
                <Label for="newAreaName">Nombre del Área (ej: Mantenimiento Ascensor)</Label>
                <Input
                  type="text"
                  id="newAreaName"
                  value={newAreaName}
                  onChange={(e) => setNewAreaName(e.target.value)}
                  placeholder="Ingrese el nombre"
                />
              </FormGroup>
              <div className="d-flex justify-content-end">
                <Button color="secondary" className="mr-2" onClick={() => setCreateAccountAreaModalOpen(false)}>
                  Cancelar
                </Button>
                <Button color="primary" onClick={handleCreateAccountingArea}>
                  Crear Área
                </Button>
              </div>
            </ModalBody>
          </Modal>

          <FormGroup>
            <Label for="description">Descripción</Label>
            <Input
              autoComplete="off"
              name="description"
              defaultValue={common?.description}
              innerRef={register({
                required: "La descripción es obligatoria.",
                minLength: {
                  value: 10,
                  message: "La descripción debe tener al menos 10 carácteres.",
                },
              })}
              type={"textarea"}
              className={`${errors.description?.message ? "border-danger" : ""
                } form-control`}
              placeholder="Descripción de la cuenta por pagar"
            />
            <WizardError
              error={errors.description?.message}
              className="fs--1 font-weight-normal d-block"
            />
          </FormGroup>
          <Row form className="pt-3">
            <Col sm tag={FormGroup}>
              <Label>Seleccione un proveedor de la lista</Label>
              <Row>
                <Col xs={12} md={""} className="d-flex mb-2 mb-3">
                  <Input
                    type="text"
                    value={providerSearch}
                    placeholder="Buscar por nombre, cédula, teléfono, correo, descripción o dirección..."
                    className="d-inline-block search-input mr-2"
                    onKeyPress={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        applyProviderSearch(event.target.value);
                      }
                    }}
                    onChange={(event) => {
                      const value = event.target.value;
                      setProviderSearch(value);
                      if (!value.trim()) {
                        setParams((prev) => ({ ...prev, search: null }));
                      }
                    }}
                  />
                  <ButtonIcon
                    icon={faSearch}
                    className="d-inline-block text-nowrap mr-2"
                    color="falcon-default"
                    type="button"
                    onClick={() => applyProviderSearch()}
                  >
                    Buscar
                  </ButtonIcon>
                  <Button
                    color="primary"
                    type="button"
                    className="d-inline-block text-nowrap"
                    onClick={() => setCreateProviderModalOpen(true)}
                  >
                    Nuevo proveedor
                  </Button>
                </Col>
              </Row>
              <Row className="m-0 p-0">
                <Col sm={12} tag={FormGroup} className="m-0 p-0">
                  {!debttopayId
                    ? renderProvidersItems()
                    : renderProvidersItemsAdd()}
                </Col>
              </Row>
              <FullPagination
                nextPage={nextPage}
                prevPage={previousPage}
                goToPage={goToPage}
                meta={meta}
                className={"shadow-none"}
                posText={"proveedores"}
                perPage={perPage}
              />
            </Col>
          </Row>

          <Modal isOpen={createProviderModalOpen} toggle={() => setCreateProviderModalOpen(false)}>
            <ModalBody>
              <CreateProviderForm
                condominiumId={id}
                handleSubmitData={handleCreateProviderSubmit}
                setRedirect={() => { }}
                setRedirectUrl={() => { }}
                onCancel={() => setCreateProviderModalOpen(false)}
              />
            </ModalBody>
          </Modal>

          <Label for="value">Monto total</Label>
          <Row form>
            <Col sm={6} tag={FormGroup}>
              <Controller
                name="value"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Ingresar el monto",
                  },
                }}
                render={({ onChange }) => (
                  <NumberFormat
                    name="value"
                    defaultValue={common?.value || undefined}
                    getInputRef={register("value", {
                      required: "Ingresar el monto",
                    })}
                    onValueChange={({ value }) => {
                      setValue("value", Number(value));
                      onChange(`${value}`);
                    }}
                    value={watch("value")}
                    thousandSeparator
                    prefix={`${baseCurrency.symbol} `}
                    fixedDecimalScale
                    decimalScale={2}
                    className={`${errors.value?.message ? "border-danger" : ""
                      } form-control`}
                    placeholder="Monto de la cuenta por pagar"
                  />
                )}
              />

              <WizardError
                error={errors.value?.message}
                className="fs--1 font-weight-normal d-block"
              />
            </Col>
          </Row>

          {!commonCost && (
            <Row
              form
              className="align-items-center justify-content-center justify-content-md-end"
            >
              <ButtonIcon
                color="falcon-default"
                className="my-2 mx-0 mx-md-2 m-md-0 px-5 mr-2"
                icon={faUndo}
                iconAlign="right"
                transform="down-1 shrink-4"
                onClick={() =>
                  history.push({
                    pathname: `/dashboard/${moduleBase}/${id}/topay`,
                  })
                }
              >
                Volver
              </ButtonIcon>
              <ButtonIcon
                data-testid="submit-button"
                color="primary"
                className="my-2 mx-0 mx-md-2 m-md-0 px-5"
                type="submit"
                icon={faSave}
                iconAlign="right"
                onClick={handleSubmit(handleSubmitData)}
                /*                 disabled={Object.keys(errors).length > 0} */
                transform="down-1 shrink-4"
              >
                Guardar
              </ButtonIcon>
            </Row>
          )}
        </CardBody>
      </>
    );
  };

  return (
    <>
      <Card
        className="bg-light mb-3"
        data-testid="form"
        tag={Form}
        onSubmit={handleSubmit(handleSubmitData)}
      >
        <PreloadState loading={isSubmitting} message={`Guardando ${state?.type_name || "cuenta por pagar"}...`}>
          {renderForm()}
        </PreloadState>
      </Card>
    </>
  );
};

export default withRedirect(CreateAccountPayForm);
